import { useEffect, useState } from "react";
import { isKeyboardSummoningFocus } from "@/lib/keyboardFocus";

const vv = window.visualViewport;

/**
 * When focus leaves an editable, the keyboard is still sliding out —
 * report it closed only once it has had time to clear the screen, so the
 * mobile nav pill doesn't reappear behind the (translucent) keyboard.
 * Also absorbs blur→refocus hops between fields.
 */
const FOCUS_CLOSE_DELAY_MS = 250;

/**
 * While we believe a keyboard is up, re-check on a timer — the same
 * backstop `useIosViewportGuard` runs, for the same reason. Unmounting
 * the focused field during a route change takes the keyboard down
 * without a dependable `focusout`, and if WebKit also swallows the
 * resize, nothing would ever recompute: the nav pill would stay hidden
 * (and its reserved space surrendered) for the life of the app. Only
 * armed while open, so it costs nothing when no keyboard is on screen.
 */
const WATCHDOG_MS = 500;

type UseDetectKeyboardOpenProps = {
  minKeyboardHeight?: number;
  defaultValue?: boolean;
  delay?: number;
};

const useDetectKeyboardOpen = ({
  minKeyboardHeight = 300,
  defaultValue = false,
  delay = 300,
}: UseDetectKeyboardOpenProps) => {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(defaultValue);

  useEffect(() => {
    let timer = 0;

    const isShrunk = () =>
      !!vv && window.screen.height - minKeyboardHeight > vv.height;

    const compute = () =>
      !!vv &&
      // Pinch zoom also shrinks visualViewport.height (≈ layout height /
      // scale) — on an iPhone a ~1.2× zoom already crosses the keyboard
      // threshold, misreporting "keyboard open" (and e.g. hiding the mobile
      // nav) while the user is just zooming. While zoomed, treat the
      // keyboard as closed: fixed-element geometry is native-pan territory
      // anyway, and useIosViewportGuard disarms on zoom for the same
      // reason. Same 1.01 tolerance as the guard — a quirk reporting scale
      // slightly below 1 must not disable detection.
      (vv.scale ?? 1) <= 1.01 &&
      isShrunk() &&
      // The focus check is the un-stick: iOS shows no keyboard without a
      // focused editable, so it outranks a `visualViewport.height` that
      // WebKit never corrected after a dismissal it failed to announce.
      // Without it the nav pill stayed hidden (and its space stayed
      // surrendered) for the life of the app — half of the dead-bottom-band
      // report from prod v1.1.29.
      isKeyboardSummoningFocus();

    const listener = () => {
      if (!vv) {
        return;
      }
      window.clearTimeout(timer);
      const newState = compute();
      if (isKeyboardOpen === newState) {
        return;
      }
      timer = window.setTimeout(
        // Re-read at fire time: focus can hop back to another field inside
        // the delay, and a decision made 250ms ago must not overwrite it.
        () => setIsKeyboardOpen(compute()),
        newState
          ? // Taking into account the time it takes for the keyboard to open
            delay
          : // A viewport that already reports full height is proof the
            // keyboard is gone — no reason to wait. Anything else is a
            // focus-driven close mid-retract.
            isShrunk()
            ? FOCUS_CLOSE_DELAY_MS
            : 0,
      );
    };
    /**
     * Resume / BFCache restore / rotation: WebKit does not reliably fire
     * a `visualViewport` resize across any of them, and a keyboard
     * dismissed while we were away produces no focus event either — so
     * re-derive from what is true now instead of waiting for an event
     * that may never arrive.
     */
    const resync = () => {
      if (document.visibilityState === "hidden") {
        return;
      }
      listener();
    };

    if (vv) {
      vv.addEventListener("resize", listener);
    }
    // Focus changes move the answer on their own now, and they are the
    // signal that survives when the viewport resize goes missing.
    window.addEventListener("focusin", listener);
    window.addEventListener("focusout", listener);
    document.addEventListener("visibilitychange", resync);
    window.addEventListener("pageshow", resync);
    window.addEventListener("orientationchange", resync);
    window.addEventListener("resize", listener);
    // Last resort for a dismissal that fires no event at all.
    const watchdog = isKeyboardOpen
      ? window.setInterval(listener, WATCHDOG_MS)
      : 0;
    return () => {
      window.clearTimeout(timer);
      window.clearInterval(watchdog);
      if (vv) {
        vv.removeEventListener("resize", listener);
      }
      window.removeEventListener("focusin", listener);
      window.removeEventListener("focusout", listener);
      document.removeEventListener("visibilitychange", resync);
      window.removeEventListener("pageshow", resync);
      window.removeEventListener("orientationchange", resync);
      window.removeEventListener("resize", listener);
    };
  }, [isKeyboardOpen, minKeyboardHeight, delay]);

  return isKeyboardOpen;
};

export default useDetectKeyboardOpen;
